import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaSyncAlt, FaDownload, FaDatabase, FaSearch, 
  FaTimes, FaTimesCircle, FaGlobe, FaMobileAlt,
  FaLaptop, FaUserSecret, FaRegClock, FaCalendarAlt,
  FaMapMarkerAlt, FaLink, FaHistory, FaEye
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Google Sheets Web App URL for Page Hits (using GET connection)
const GOOGLE_SHEETS_HITS_URL = 'https://script.google.com/macros/s/AKfycbynPnm0tWszx4McNUHGpQkZ-QvzJRaidiHEwcwUb9fYlTRnO7Pk7xRF4ZG4bLBUTY-f/exec';

// Realistic mockup data for demo mode
const MOCKUP_HITS = [
  {
    "Timestamp": "2026-06-09T14:23:10.000Z",
    "Session ID": "sess_a8f3kd9s",
    "Page Path": "/",
    "Referrer": "https://www.google.com/",
    "IP Address": "103.241.12.89",
    "Country": "India",
    "City": "Chennai",
    "Region": "Tamil Nadu",
    "Browser": "Chrome",
    "OS": "Windows",
    "Screen Resolution": "1920x1080"
  },
  {
    "Timestamp": "2026-06-09T14:27:01.000Z",
    "Session ID": "sess_a8f3kd9s", // Same session (Returning hit)
    "Page Path": "/",
    "Referrer": "https://www.google.com/",
    "IP Address": "103.241.12.89",
    "Country": "India",
    "City": "Chennai",
    "Region": "Tamil Nadu",
    "Browser": "Chrome",
    "OS": "Windows",
    "Screen Resolution": "1920x1080"
  },
  {
    "Timestamp": "2026-06-09T14:29:45.000Z",
    "Session ID": "sess_a8f3kd9s", // Third hit from same session
    "Page Path": "/",
    "Referrer": "https://www.google.com/",
    "IP Address": "103.241.12.89",
    "Country": "India",
    "City": "Chennai",
    "Region": "Tamil Nadu",
    "Browser": "Chrome",
    "OS": "Windows",
    "Screen Resolution": "1920x1080"
  },
  {
    "Timestamp": "2026-06-09T14:25:15.000Z",
    "Session ID": "sess_b93kd8sa",
    "Page Path": "/",
    "Referrer": "Direct",
    "IP Address": "182.72.19.124",
    "Country": "India",
    "City": "Coimbatore",
    "Region": "Tamil Nadu",
    "Browser": "Safari",
    "OS": "iOS",
    "Screen Resolution": "390x844"
  },
  {
    "Timestamp": "2026-06-09T14:30:50.000Z",
    "Session ID": "sess_c84jdn8a",
    "Page Path": "/",
    "Referrer": "https://t.co/",
    "IP Address": "8.8.8.8",
    "Country": "United States",
    "City": "Mountain View",
    "Region": "California",
    "Browser": "Chrome",
    "OS": "Linux",
    "Screen Resolution": "1440x900"
  },
  {
    "Timestamp": "2026-06-09T14:35:12.000Z",
    "Session ID": "sess_d39kd8ha",
    "Page Path": "/",
    "Referrer": "Direct",
    "IP Address": "157.44.201.52",
    "Country": "India",
    "City": "Mumbai",
    "Region": "Maharashtra",
    "Browser": "Edge",
    "OS": "Windows",
    "Screen Resolution": "1366x768"
  }
];

const formatTimestamp = (ts) => {
  if (!ts) return 'N/A';
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) {
      return ts;
    }
    // If it's already a formatted string without ISO characters, keep it
    if (typeof ts === 'string' && !ts.includes('T') && !ts.includes('Z')) {
      return ts;
    }
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (e) {
    return ts;
  }
};

const PageHitsAdmin = () => {
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [countryFilter, setCountryFilter] = useState('all');
  const [browserFilter, setBrowserFilter] = useState('all');
  const [selectedSession, setSelectedSession] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, countryFilter, browserFilter]);

  const fetchHits = async (forceDemo = false) => {
    setLoading(true);
    setHasError(false);

    if (forceDemo || !GOOGLE_SHEETS_HITS_URL) {
      setTimeout(() => {
        setHits(MOCKUP_HITS);
        setLoading(false);
        setIsDemoMode(true);
        toast.info("Loaded demo tracking simulator data!");
      }, 800);
      return;
    }

    try {
      const response = await fetch(GOOGLE_SHEETS_HITS_URL);
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }
      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format from Apps Script. Array expected.");
      }

      // Normalize sheet columns
      const formatted = data.map(item => ({
        Timestamp: item["Timestamp (IST)"] || item["Timestamp"] || item.Timestamp || item.timestamp || new Date().toISOString(),
        sessionId: item["Session ID"] || item["sessionId"] || item.sessionId || "",
        path: item["Page Path"] || item["path"] || item.path || "/",
        referrer: item["Referrer"] || item["referrer"] || item.referrer || "Direct",
        ip: item["IP Address"] || item["ip"] || item.ip || "",
        country: item["Country"] || item["country"] || item.country || "",
        city: item["City"] || item["city"] || item.city || "",
        region: item["Region"] || item["region"] || item.region || "",
        browser: item["Browser"] || item["browser"] || item.browser || "",
        os: item["OS"] || item["os"] || item.os || "",
        screenResolution: item["Screen Resolution"] || item["screenResolution"] || item.screenResolution || ""
      }));

      setHits(formatted);
      setIsDemoMode(false);
      toast.success("Synchronized page hit tracking live!");
    } catch (err) {
      console.error("Live fetch error:", err);
      setHasError(true);
      setHits(MOCKUP_HITS);
      setIsDemoMode(true);
      toast.warning("Failed to connect live. Failover to Sandbox simulation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHits();
  }, []);

  // Group hits by Session ID to display them as unique visitors, keeping track of history
  const groupedSessions = useMemo(() => {
    const sessionsMap = {};

    hits.forEach(hit => {
      const sessId = hit.sessionId || 'Unknown';
      if (!sessionsMap[sessId]) {
        sessionsMap[sessId] = {
          sessionId: sessId,
          hitsCount: 0,
          latestHit: null,
          history: []
        };
      }
      
      sessionsMap[sessId].hitsCount += 1;
      sessionsMap[sessId].history.push(hit);
      
      // Determine latest hit by comparing timestamps
      if (!sessionsMap[sessId].latestHit || new Date(hit.Timestamp) > new Date(sessionsMap[sessId].latestHit.Timestamp)) {
        sessionsMap[sessId].latestHit = hit;
      }
    });

    // Sort history chronologically (latest first)
    Object.values(sessionsMap).forEach(sess => {
      sess.history.sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));
    });

    // Return list of sessions sorted by the latest hit's timestamp (descending)
    return Object.values(sessionsMap).sort((a, b) => new Date(b.latestHit.Timestamp) - new Date(a.latestHit.Timestamp));
  }, [hits]);

  // Filter sessions logic
  const filteredSessions = useMemo(() => {
    return groupedSessions.filter(session => {
      const h = session.latestHit;
      const searchString = `${session.sessionId} ${h.ip} ${h.country} ${h.city} ${h.region} ${h.browser} ${h.os} ${h.referrer}`.toLowerCase();
      const matchesSearch = searchString.includes(searchQuery.toLowerCase());

      const matchesCountry = countryFilter === 'all' || h.country.toLowerCase() === countryFilter.toLowerCase();
      const matchesBrowser = browserFilter === 'all' || h.browser.toLowerCase() === browserFilter.toLowerCase();

      return matchesSearch && matchesCountry && matchesBrowser;
    });
  }, [groupedSessions, searchQuery, countryFilter, browserFilter]);

  // Unique lists for dropdowns based on all hits
  const uniqueCountries = useMemo(() => Array.from(new Set(hits.map(h => h.country).filter(Boolean))), [hits]);
  const uniqueBrowsers = useMemo(() => Array.from(new Set(hits.map(h => h.browser).filter(Boolean))), [hits]);

  // Calculate Metrics
  const totalPageHitsCount = useMemo(() => filteredSessions.reduce((acc, sess) => acc + sess.hitsCount, 0), [filteredSessions]);
  const uniqueVisitorsCount = filteredSessions.length;

  // Group top cities based on latest hits of active sessions
  const sortedLocations = useMemo(() => {
    const topLocations = {};
    filteredSessions.forEach(sess => {
      const h = sess.latestHit;
      if (h.city && h.city !== 'Unknown') {
        const label = `${h.city}, ${h.country}`;
        topLocations[label] = (topLocations[label] || 0) + 1;
      }
    });
    return Object.entries(topLocations)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  }, [filteredSessions]);

  // Export to CSV (Exporting the detailed hits)
  const exportToCSV = () => {
    if (filteredSessions.length === 0) {
      toast.error("No data available to export");
      return;
    }

    const headers = [
      "Timestamp (IST)", "Session ID", "Page Path", "Referrer", 
      "IP Address", "Country", "City", "Region", "Browser", "OS", "Screen Resolution"
    ];

    const safeCell = (val) => {
      if (val === null || val === undefined) return '""';
      return `"${String(val).replace(/"/g, '""')}"`;
    };

    const csvRows = [headers.join(",")];

    filteredSessions.forEach(session => {
      session.history.forEach(h => {
        const row = [
          safeCell(h.Timestamp),
          safeCell(h.sessionId),
          safeCell(h.path),
          safeCell(h.referrer),
          safeCell(h.ip),
          safeCell(h.country),
          safeCell(h.city),
          safeCell(h.region),
          safeCell(h.browser),
          safeCell(h.os),
          safeCell(h.screenResolution)
        ];
        csvRows.push(row.join(","));
      });
    });

    const blob = new Blob(["\ufeff" + csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `iMatrix_Page_Hits_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Hits history database exported successfully!");
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentSessions = filteredSessions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);

  return (
    <div className="admin-root">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* ── Dashboard Header ── */}
      <header className="admin-header">
        <div className="admin-header-title">
          <span className="admin-badge">
            <span className={`live-dot ${!isDemoMode ? 'animate-pulse' : ''}`} style={{ backgroundColor: isDemoMode ? '#e2e8f0' : '#10b981' }} />
            {isDemoMode ? 'Sandbox Simulator' : 'Live Connected'}
          </span>
          <h1>Web Traffic Dashboard</h1>
          <p>Real-time tracking of visitor hits and geographical demographics (Grouped by unique sessions)</p>
        </div>

        <div className="admin-actions">
          <button 
            className="refresh-btn" 
            onClick={() => fetchHits(false)} 
            disabled={loading}
            title="Sync Live with Google Sheet"
          >
            <FaSyncAlt className={loading ? 'loading-spin' : ''} /> <span>Sync Live</span>
          </button>

          <button 
            className="export-btn" 
            onClick={exportToCSV}
            title="Export database as CSV"
          >
            <FaDownload /> <span>Export CSV</span>
          </button>
        </div>
      </header>

      {/* ── Sub Navigation Links for Admin Dashboards ── */}
      <nav className="admin-subnav" style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        borderBottom: '2px solid var(--border, #e2e8f0)',
        paddingBottom: '12px'
      }}>
        <Link to="/admin/superadmin" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Careers Portal
        </Link>
        <Link to="/admin/contactus" style={{
          textDecoration: 'none',
          color: 'var(--text-secondary, #64748b)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid transparent',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Contact Messages
        </Link>
        <Link to="/admin/pagehits" style={{
          textDecoration: 'none',
          color: 'var(--brand, #6b7a52)',
          fontWeight: '700',
          fontSize: '1.05rem',
          borderBottom: '3px solid var(--brand, #6b7a52)',
          paddingBottom: '10px',
          transition: 'all 0.2s'
        }}>
          Page Hits Tracker
        </Link>
      </nav>

      {/* ── Key Metrics Cards ── */}
      <section className="metrics-dashboard">
        <div className="metric-card">
          <div className="metric-icon-wrap user-icon">
            <FaRegClock />
          </div>
          <div className="metric-info">
            <span className="metric-label">Total Hits / Page Loads</span>
            <h2 className="metric-value">{totalPageHitsCount}</h2>
            <span className="metric-trend green">Total Home view events</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon-wrap experience-icon">
            <FaLaptop />
          </div>
          <div className="metric-info">
            <span className="metric-label">Unique Visitors</span>
            <h2 className="metric-value">{uniqueVisitorsCount}</h2>
            <span className="metric-trend blue">Distinct unique sessions</span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon-wrap notice-icon">
            <FaGlobe />
          </div>
          <div className="metric-info">
            <span className="metric-label">Top Location Hub</span>
            <h2 className="metric-value" style={{ fontSize: '1.45rem', marginTop: '10px', marginBottom: '10px' }}>
              {sortedLocations[0] ? sortedLocations[0][0] : 'N/A'}
            </h2>
            <span className="metric-trend gold">
              {sortedLocations[0] ? `${sortedLocations[0][1]} active sessions` : 'No data logged'}
            </span>
          </div>
        </div>
      </section>

      {/* ── Filter Controls Section ── */}
      <section className="filter-controls-card">
        <div className="search-box-wrap">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Search sessions by Session ID, IP, Country, City, Referrer, Browser, or OS..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FaTimesCircle />
            </button>
          )}
        </div>

        <div className="filters-dropdowns-wrap">
          <div className="filter-select-group">
            <label>Country Origin</label>
            <select 
              value={countryFilter} 
              onChange={(e) => setCountryFilter(e.target.value)}
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map((country, idx) => (
                <option key={idx} value={country}>{country}</option>
              ))}
            </select>
          </div>

          <div className="filter-select-group">
            <label>Browser Engine</label>
            <select 
              value={browserFilter} 
              onChange={(e) => setBrowserFilter(e.target.value)}
            >
              <option value="all">All Browsers</option>
              {uniqueBrowsers.map((browser, idx) => (
                <option key={idx} value={browser}>{browser}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* ── Hits Table Grid ── */}
      <main className="candidates-table-card">
        {loading ? (
          <div className="table-loading-state">
            <FaSyncAlt className="loading-spin" />
            <p>Syncing visitor metrics...</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="table-empty-state">
            <FaTimesCircle />
            <h3>No Unique Sessions Found</h3>
            <p>Try modifying your search query or filters</p>
          </div>
        ) : (
          <div className="table-responsive-wrapper">
            <table className="candidates-table">
              <thead>
                <tr>
                  <th>Latest Visit (IST)</th>
                  <th>Session ID / IP</th>
                  <th>Origin / Geolocation</th>
                  <th>Hit Count</th>
                  <th>Latest Referrer</th>
                  <th>Environment</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentSessions.map((session, idx) => {
                  const h = session.latestHit;
                  return (
                    <tr key={idx} onClick={() => setSelectedSession(session)}>
                      <td>
                        <div className="detail-row">
                          <FaCalendarAlt />
                          <span>{formatTimestamp(h.Timestamp)}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <h4 className="candidate-name" style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
                            {session.sessionId}
                          </h4>
                          <span className="candidate-timestamp" style={{ fontSize: '0.8rem' }}>
                            IP: {h.ip || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="contact-details-cell">
                          <span className="detail-row" style={{ fontWeight: '600' }}>
                            <FaMapMarkerAlt /> {h.city || 'Unknown'}, {h.country || 'Unknown'}
                          </span>
                          {h.region && h.region !== 'Unknown' && (
                            <span style={{ fontSize: '0.78rem', color: '#64748b', marginLeft: '18px' }}>
                              Region: {h.region}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="exp-badge" style={{ 
                          background: 'rgba(107, 122, 82, 0.12)', 
                          color: 'var(--brand, #6b7a52)', 
                          border: '1px solid rgba(107, 122, 82, 0.3)',
                          cursor: 'pointer',
                          fontWeight: '800'
                        }} title="Click to view history logs" onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSession(session);
                        }}>
                          {session.hitsCount} hits
                        </span>
                      </td>
                      <td>
                        <div className="detail-row" style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <FaLink />
                          <span title={h.referrer}>{h.referrer}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span className="exp-badge" style={{ background: '#f1f5f9', color: '#1e293b', border: '1px solid #cbd5e1' }}>
                            {h.browser}
                          </span>
                          <span className="exp-badge" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>
                            {h.os}
                          </span>
                        </div>
                      </td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <button 
                          className="view-details-btn" 
                          onClick={() => setSelectedSession(session)}
                        >
                          Review Session
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-controls" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderTop: '1px solid var(--border, #e2e8f0)',
            background: 'var(--surface, #ffffff)',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #64748b)', fontWeight: '600' }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSessions.length)} of {filteredSessions.length} records
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border, #e2e8f0)',
                  background: currentPage === 1 ? 'var(--bg-subtle, #f1f5f9)' : 'var(--surface, #ffffff)',
                  color: currentPage === 1 ? '#94a3b8' : 'var(--text-primary, #1e293b)',
                  fontWeight: '700',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s'
                }}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, index, array) => {
                  return (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span style={{ alignSelf: 'center', padding: '0 4px', color: '#94a3b8' }}>...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(page)}
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '8px',
                          border: currentPage === page ? '1px solid var(--brand, #6b7a52)' : '1px solid var(--border, #e2e8f0)',
                          background: currentPage === page ? 'var(--brand, #6b7a52)' : 'var(--surface, #ffffff)',
                          color: currentPage === page ? '#ffffff' : 'var(--text-primary, #1e293b)',
                          fontWeight: '700',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s'
                        }}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border, #e2e8f0)',
                  background: currentPage === totalPages ? 'var(--bg-subtle, #f1f5f9)' : 'var(--surface, #ffffff)',
                  color: currentPage === totalPages ? '#94a3b8' : 'var(--text-primary, #1e293b)',
                  fontWeight: '700',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  transition: 'all 0.2s'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>

      {/* ── Hit Detail Modal Drawer ── */}
      {selectedSession && (
        <div className="modal-overlay" onClick={() => setSelectedSession(null)}>
          <div className="modal-card" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
            <header className="modal-header">
              <div className="modal-title-wrap">
                <div className="candidate-avatar large" style={{ background: 'var(--brand, #6b7a52)' }}>
                  <FaUserSecret />
                </div>
                <div>
                  <h2>Visitor Session Details</h2>
                  <p className="modal-subtitle">Session ID: {selectedSession.sessionId} • Total Hits: {selectedSession.hitsCount}</p>
                </div>
              </div>
              <button className="close-modal-btn" onClick={() => setSelectedSession(null)}>
                <FaTimes />
              </button>
            </header>

            <div className="modal-body-content">
              {/* Technical Visit Information */}
              <div className="modal-info-section">
                <h3>Latest Hit Metadata</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Latest Hit (IST)</span>
                    <span className="field-value">{formatTimestamp(selectedSession.latestHit.Timestamp)}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">IP Address</span>
                    <span className="field-value">{selectedSession.latestHit.ip || 'Unknown'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Screen Resolution</span>
                    <span className="field-value">{selectedSession.latestHit.screenResolution || 'N/A'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Landing Page</span>
                    <span className="field-value">{selectedSession.latestHit.path || '/'}</span>
                  </div>
                </div>
              </div>

              {/* Geographic Data */}
              <div className="modal-info-section">
                <h3>Latest Geographic Location Details</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Country</span>
                    <span className="field-value">{selectedSession.latestHit.country || 'Unknown'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">City</span>
                    <span className="field-value">{selectedSession.latestHit.city || 'Unknown'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Region / State</span>
                    <span className="field-value">{selectedSession.latestHit.region || 'Unknown'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Latest Referrer Source</span>
                    <span className="field-value" style={{ wordBreak: 'break-all' }}>{selectedSession.latestHit.referrer}</span>
                  </div>
                </div>
              </div>

              {/* Environment */}
              <div className="modal-info-section">
                <h3>Platform & Environment</h3>
                <div className="modal-grid-fields">
                  <div className="field-group">
                    <span className="field-label">Browser Engine</span>
                    <span className="field-value">{selectedSession.latestHit.browser || 'Unknown'}</span>
                  </div>
                  <div className="field-group">
                    <span className="field-label">Operating System</span>
                    <span className="field-value">{selectedSession.latestHit.os || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Session History logs (Timeline view) */}
              <div className="modal-info-section full-width">
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaHistory /> Session Visit History logs ({selectedSession.history.length})
                </h3>
                <div style={{ 
                  background: 'var(--bg-subtle, #f1f5f9)', 
                  border: '1px solid var(--border, #e2e8f0)', 
                  borderRadius: '16px', 
                  padding: '20px',
                  maxHeight: '260px',
                  overflowY: 'auto'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #cbd5e1', color: 'var(--brand, #6b7a52)', fontWeight: 'bold' }}>
                        <th style={{ padding: '8px 12px' }}>Visit Timestamp (IST)</th>
                        <th style={{ padding: '8px 12px' }}>Referrer Source</th>
                        <th style={{ padding: '8px 12px' }}>Resolution</th>
                        <th style={{ padding: '8px 12px' }}>Browser/OS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSession.history.map((h, hIdx) => (
                        <tr key={hIdx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px 12px', fontWeight: '500' }}>{formatTimestamp(h.Timestamp)}</td>
                          <td style={{ padding: '10px 12px', wordBreak: 'break-all' }}>{h.referrer}</td>
                          <td style={{ padding: '10px 12px' }}>{h.screenResolution || 'N/A'}</td>
                          <td style={{ padding: '10px 12px' }}>{h.browser} on {h.os}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <footer className="modal-footer-actions">
              <button className="dismiss-btn" onClick={() => setSelectedSession(null)}>Dismiss Log</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHitsAdmin;
